package com.ijoin.ui.billing

import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import com.ijoin.data.model.BillingOverview
import com.ijoin.data.model.PayoutMethod
import com.ijoin.data.repository.BillingFilter
import com.ijoin.data.repository.BillingRepository
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.launch

class BillingViewModel(
    @Suppress("unused") private val userId: String,
    private val repository: BillingRepository = BillingRepository()
) : ViewModel() {

    private val _uiState = MutableStateFlow(BillingUiState())
    val uiState: StateFlow<BillingUiState> = _uiState.asStateFlow()

    init {
        load()
    }

    fun selectFilter(filter: BillingFilter) {
        if (_uiState.value.selectedFilter == filter) return
        _uiState.update { it.copy(selectedFilter = filter) }
        load(filter)
    }

    fun refresh() {
        load(_uiState.value.selectedFilter)
    }

    fun requestWithdrawal(
        method: PayoutMethod,
        details: Map<String, String>,
        note: String? = null
    ) {
        val overview = _uiState.value.overview ?: return
        val amount = overview.summary.currentBalance
        if (amount.value <= 0.0) {
            _uiState.update { it.copy(message = BillingMessage.WithdrawUnavailable) }
            return
        }

        viewModelScope.launch {
            _uiState.update { it.copy(isCreatingPayout = true, error = null) }
            repository.requestWithdrawal(
                amount = amount,
                method = method,
                details = details,
                note = note
            )
                .onSuccess {
                    _uiState.update {
                        it.copy(
                            isCreatingPayout = false,
                            message = BillingMessage.WithdrawSuccess
                        )
                    }
                    load(_uiState.value.selectedFilter)
                }
                .onFailure { error ->
                    _uiState.update {
                        it.copy(
                            isCreatingPayout = false,
                            error = error.message,
                            message = BillingMessage.WithdrawFailed
                        )
                    }
                }
        }
    }

    fun clearMessage() {
        _uiState.update { it.copy(message = null) }
    }

    private fun load(filter: BillingFilter = _uiState.value.selectedFilter) {
        viewModelScope.launch {
            _uiState.update { it.copy(isLoading = true, error = null) }
            repository.loadBilling(filter)
                .onSuccess { overview ->
                    _uiState.update {
                        it.copy(
                            isLoading = false,
                            overview = overview,
                            error = null,
                            selectedFilter = filter
                        )
                    }
                }
                .onFailure { error ->
                    _uiState.update {
                        it.copy(
                            isLoading = false,
                            error = error.message
                        )
                    }
                }
        }
    }
}

data class BillingUiState(
    val isLoading: Boolean = true,
    val error: String? = null,
    val overview: BillingOverview? = null,
    val selectedFilter: BillingFilter = BillingFilter.THIS_MONTH,
    val isCreatingPayout: Boolean = false,
    val message: BillingMessage? = null
)

sealed interface BillingMessage {
    data object WithdrawSuccess : BillingMessage
    data object WithdrawFailed : BillingMessage
    data object WithdrawUnavailable : BillingMessage
}
